import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";

const EFFECTIVE_DATE = "February 12, 2026";

export default function Terms() {
    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-20 px-6">
                <article className="max-w-3xl mx-auto glass p-8 sm:p-12 fade-in">
                    <p className="label-tracking text-gold">Legal</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-2 mb-2">Terms of Service</h1>
                    <p className="text-muted text-sm">Effective {EFFECTIVE_DATE}</p>

                    <div className="mt-10 space-y-8 text-sm sm:text-base leading-relaxed">
                        <div>
                            <p className="text-muted">
                                Welcome to <strong className="text-ink">SoulStar Dating</strong> ("SoulStar", "we", "us", "our").
                                These Terms of Service ("Terms") govern your access to and use of our website, mobile
                                interfaces, and services (collectively, the "Service"). By creating an account or using
                                the Service you accept and agree to be bound by these Terms and by our Privacy Policy.
                                If you do not agree, do not use the Service.
                            </p>
                        </div>

                        <Section title="1. Eligibility">
                            <p>You must be at least <strong>18 years old</strong> to use SoulStar. By using the Service
                            you represent and warrant that you are 18+, are legally able to enter into a binding
                            contract, are not barred from receiving services under the laws of any applicable jurisdiction,
                            and have never been convicted of a felony, indictable offense, sex crime, or crime of violence.</p>
                        </Section>

                        <Section title="2. Your Account">
                            <p>You are responsible for the accuracy of the information you provide (including birth
                            date, time, and location, which are used to compute your astrological chart) and for
                            keeping your password confidential. You may not share your account, impersonate anyone,
                            or use another user's account without permission. You must promptly notify us of any
                            unauthorized access at <em>support@soulstar.love</em>.</p>
                        </Section>

                        <Section title="3. Astrology & AI Content">
                            <p>Horoscopes, birth-chart interpretations, compatibility scores, and AI-generated readings
                            are provided <strong>for entertainment and self-reflection purposes only</strong>. They are
                            not, and should not be relied upon as, medical, mental-health, financial, relationship, or
                            legal advice. SoulStar makes no warranty as to the accuracy, completeness, or timeliness
                            of any astrological or AI-generated content.</p>
                        </Section>

                        <Section title="4. User Content & License">
                            <p>You retain ownership of the photos, bios, messages, and other content you submit
                            ("User Content"). By submitting User Content, you grant SoulStar a worldwide, non-exclusive,
                            royalty-free, sublicensable license to host, store, reproduce, display, and distribute your
                            User Content <em>solely</em> for the purpose of operating and providing the Service (for
                            example, showing your profile to other users, generating a shareable social card, or
                            training internal moderation classifiers). We will not sell your User Content.</p>
                        </Section>

                        <Section title="5. Prohibited Conduct">
                            <p>You agree not to: (a) use the Service if under 18; (b) upload photos of anyone other
                            than yourself, or photos containing nudity, minors, weapons, hate imagery, or illegal
                            content; (c) harass, stalk, threaten, dox, or defraud any user; (d) solicit money, goods,
                            or personal information; (e) use the Service for any commercial purpose (advertising,
                            escort, sugar-dating, MLM); (f) scrape, reverse-engineer, or otherwise interfere with the
                            Service; (g) create more than one account; or (h) upload content that infringes any
                            third-party right. Violations may result in immediate termination and, where appropriate,
                            reporting to law enforcement.</p>
                        </Section>

                        <Section title="6. Selfie Verification & Biometrics">
                            <p>SoulStar offers an optional selfie-verification feature. If you use it, your live
                            selfie is compared to your profile photo using a Vision AI model to compute a match
                            confidence score. The selfie and score are <strong>never sold</strong>, are stored only
                            long enough to complete verification and any dispute resolution, and are deleted on
                            request. If you reside in a jurisdiction with biometric laws (e.g., Illinois BIPA,
                            Texas CUBI, Washington HB 1493), your continued use of the verification feature
                            constitutes written consent to the collection, use, and destruction schedule
                            described in our Privacy Policy.</p>
                        </Section>

                        <Section title="7. Subscriptions, Payments & Refunds">
                            <p>Certain features (search, direct messaging before matching, birth-chart PDF, AI
                            synastry reports) require a paid subscription. Prices are shown at checkout in USD. Payment
                            is processed by <strong>Stripe, Inc.</strong>; by paying you also accept Stripe's terms.
                            Subscriptions auto-renew until cancelled from Profile → Subscription. Refund policy:
                            <strong> new subscribers may request a full refund within 7 days of first purchase</strong>
                            by emailing <em>support@soulstar.love</em>. After 7 days, or for renewals, fees are
                            non-refundable except where required by law.</p>
                        </Section>

                        <Section title="8. Safety Disclaimer">
                            <p>SoulStar does <strong>not</strong> conduct criminal-background checks. Meeting people
                            from the internet carries risk. Always tell a friend where you'll be, meet in a public
                            place, and never send money to another user. If you feel unsafe, contact local law
                            enforcement immediately. To the fullest extent permitted by law, SoulStar disclaims all
                            liability for the actions of other users.</p>
                        </Section>

                        <Section title="9. Termination">
                            <p>You may delete your account at any time from Profile → Delete Account. We may suspend
                            or terminate your account at our discretion for any breach of these Terms, suspected fraud,
                            or upon reasonable notice for the discontinuation of the Service.</p>
                        </Section>

                        <Section title="10. Disclaimer of Warranties">
                            <p className="uppercase text-xs tracking-wide">
                            The Service is provided "as is" and "as available" without warranty of any kind, whether
                            express, implied, statutory, or otherwise. To the maximum extent permitted by law, SoulStar
                            disclaims all warranties, including merchantability, fitness for a particular purpose, and
                            non-infringement.
                            </p>
                        </Section>

                        <Section title="11. Limitation of Liability">
                            <p className="uppercase text-xs tracking-wide">
                            To the maximum extent permitted by law, SoulStar's aggregate liability for any claim arising
                            out of or relating to the Service will not exceed the greater of (a) the amount you paid us
                            in the 12 months preceding the claim or (b) US $100. SoulStar will not be liable for any
                            indirect, incidental, consequential, special, or punitive damages.
                            </p>
                        </Section>

                        <Section title="12. Dispute Resolution & Arbitration">
                            <p>Any dispute arising under these Terms will be resolved by <strong>binding individual
                            arbitration</strong> administered by the American Arbitration Association under its Consumer
                            Arbitration Rules. You and SoulStar waive the right to a jury trial and to participate in a
                            class action. You may opt out of arbitration within 30 days of first accepting these Terms
                            by emailing <em>legal@soulstar.love</em> with subject "Arbitration Opt-Out".</p>
                        </Section>

                        <Section title="13. Governing Law">
                            <p>These Terms are governed by the laws of the State of Delaware, USA, without regard to its
                            conflict-of-laws principles, except where local consumer-protection laws apply.</p>
                        </Section>

                        <Section title="14. Changes to the Terms">
                            <p>We may update these Terms from time to time. If we make material changes we will notify
                            you by email or through the Service at least 7 days before the changes take effect. Continued
                            use of the Service after the effective date constitutes acceptance.</p>
                        </Section>

                        <Section title="15. Contact">
                            <p>Questions? <em>support@soulstar.love</em>. Legal: <em>legal@soulstar.love</em>.</p>
                        </Section>

                        <p className="text-xs text-muted mt-10 border-t border-white/10 pt-6">
                            See also our <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>.
                            <br />
                            <em>This document is a template. Please have it reviewed by counsel licensed in your
                            jurisdiction before public launch.</em>
                        </p>
                    </div>
                </article>
            </main>
        </>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h2 className="font-serif text-2xl text-ink mb-3">{title}</h2>
            <div className="text-muted">{children}</div>
        </div>
    );
}
